import { Injectable } from '@angular/core';
import QRCode from 'qrcode';

export type QrStyle = 'dots' | 'square';

export interface QrOptions {
  /** Pixel width of the returned PNG. 1024 is enough for A6 at 300dpi. */
  size?: number;
  style?: QrStyle;
  /**
   * Module colour. Defaults to Picaggo red darkened to #8E1B20.
   *
   * The brand accent itself (#EE4147) measures 3.8:1 against white — fine for
   * text on a screen, unreliable for a phone camera pointed at a printed card
   * in low light. #8E1B20 reads as the same red and measures 9.0:1.
   */
  ink?: string;
  /** Draw the Picaggo mark in the middle. */
  logo?: boolean;
}

/**
 * Draws QR codes on a canvas so we control how they look.
 *
 * The codes are normally generated server-side by qrUtils.getQRCode() and
 * cached, which gives hard black squares. This renders the same payload with
 * round modules and brand ink instead.
 *
 * It encodes whatever URL it is handed, and the caller must hand it the same
 * URL the server would: `https://i.picaggo.com/{hash}` for a collaborator code
 * and `https://i.picaggo.com/{viewer_hash}` for a view-only one. Anything else
 * splits the scan counts that pwa-qr-scan-count keeps by URL. (The test build
 * hands it app.picaggo.com/{code} instead — see core/utils/invite-link.ts.)
 *
 * This is the reference implementation for the eventual server-side change —
 * the codes are shared with iOS, Android and the TV app, so the real fix
 * belongs in the Lambda layer, not here.
 */
@Injectable({ providedIn: 'root' })
export class QrRenderService {
  /** Error correction H: tolerates ~30% damage, which pays for the centre logo. */
  private readonly ECC = 'H' as const;

  async render(text: string, opts: QrOptions = {}): Promise<string> {
    const size = opts.size ?? 1024;
    const style = opts.style ?? 'dots';
    const ink = opts.ink ?? '#8E1B20';
    const logo = opts.logo ?? true;

    const qr = QRCode.create(text, { errorCorrectionLevel: this.ECC });
    const n = qr.modules.size;
    const data = qr.modules.data;
    const on = (x: number, y: number) => !!data[y * n + x];

    // The spec asks for 4 modules of quiet zone; 2 scans reliably and leaves
    // more of the card's width for the code itself.
    const quiet = 2;
    const m = size / (n + quiet * 2);

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
    ctx.save();
    ctx.translate(quiet * m, quiet * m);

    // The three finder patterns are what make a styled code still read as a QR
    // code, so they are drawn as whole shapes rather than module by module.
    const eyes: Array<[number, number]> = [[0, 0], [n - 7, 0], [0, n - 7]];
    const inEye = (x: number, y: number) =>
      eyes.some(([ex, ey]) => x >= ex && x < ex + 7 && y >= ey && y < ey + 7);

    ctx.fillStyle = ink;
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        if (!on(x, y) || inEye(x, y)) continue;
        if (style === 'square') {
          ctx.fillRect(x * m, y * m, m, m);
        } else {
          ctx.beginPath();
          ctx.arc(x * m + m / 2, y * m + m / 2, m * 0.42, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    for (const [ex, ey] of eyes) this.drawEye(ctx, ex, ey, m, ink, style);

    if (logo) this.drawLogo(ctx, n, m, ink);

    ctx.restore();
    return canvas.toDataURL('image/png');
  }

  private roundRect(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number, r: number
  ): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  /** Outer ring plus pupil, with a radius that matches the data modules. */
  private drawEye(
    ctx: CanvasRenderingContext2D,
    ex: number, ey: number, m: number, ink: string, style: QrStyle
  ): void {
    const r = style === 'square' ? 0 : m * 1.9;
    const pr = style === 'square' ? 0 : m * 0.85;
    ctx.fillStyle = ink;
    this.roundRect(ctx, ex * m, ey * m, m * 7, m * 7, r);
    this.roundRect(ctx, (ex + 1) * m, (ey + 1) * m, m * 5, m * 5, Math.max(0, r - m * 0.55));
    ctx.fill('evenodd');
    this.roundRect(ctx, (ex + 2) * m, (ey + 2) * m, m * 3, m * 3, pr);
    ctx.fill();
  }

  /**
   * Clears a well in the middle and puts the mark in it. About 4% of the
   * code's area, well inside what error correction H can rebuild — and the
   * same proportion the Lambda already uses.
   */
  private drawLogo(ctx: CanvasRenderingContext2D, n: number, m: number, ink: string): void {
    const well = m * 7.2;
    const c = (n * m - well) / 2;

    ctx.fillStyle = '#ffffff';
    this.roundRect(ctx, c - m * 0.5, c - m * 0.5, well + m, well + m, m);
    ctx.fill();

    ctx.fillStyle = ink;
    this.roundRect(ctx, c, c, well, well, well * 0.28);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(c + well / 2, c + well / 2, well * 0.2, 0, Math.PI * 2);
    ctx.fill();
  }
}
